import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAccessToken, setRefreshToken, getAccessToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [error, setError] = useState("");
  const [serverDown, setServerDown] = useState(false);

  /**
   * ITER207: un error 503/502/504 o de red al consultar /auth/me NO debe cerrar
   * la sesión. Solo un 401/403 (token realmente inválido) desautentica.
   */
  const fetchMe = useCallback(async () => {
    for (let intento = 0; intento < 3; intento++) {
      try {
        const { data } = await api.get("/auth/me");
        setServerDown(false);
        setUser(data);
        return;
      } catch (e) {
        const st = e?.response?.status;
        if (st === 401 || st === 403) {
          setServerDown(false);
          setUser(false);
          return;
        }
        if (intento < 2) {
          await new Promise(r => setTimeout(r, 1500 * (intento + 1)));
          continue;
        }
        // Servidor no disponible: conservar la sesión si hay token guardado
        if (getAccessToken()) {
          setServerDown(true);
        } else {
          setUser(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        return { requires2FA: true, challengeToken: data.challenge_token, email: data.email };
      }
      // Store access_token and refresh_token as fallback for cross-origin cookie-blocked scenarios
      if (data.access_token) setAccessToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const login2FA = async (challengeToken, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/login", { challenge_token: challengeToken, code });
      if (data.access_token) setAccessToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setRefreshToken(null);
    setUser(false);
  };

  const hasModule = (modulo) => {
    if (!user || user === false) return false;
    if (user.role === "admin") return true;
    return (user.modulos || []).includes(modulo);
  };

  /**
   * hasSubmodule: enforcement granular por submódulo.
   * - admin: siempre true.
   * - sin acceso al módulo: false.
   * - permisos[modulo] ausente o lista (modo legacy): true (sin restricción de submódulo).
   * - permisos[modulo].submodulos vacío/ausente: true (todos).
   * - permisos[modulo].submodulos no vacío: requiere que `submodulo` esté en la lista.
   */
  const hasSubmodule = (modulo, submodulo) => {
    if (!user || user === false) return false;
    if (user.role === "admin") return true;
    if (!(user.modulos || []).includes(modulo)) return false;
    if (!submodulo) return true;
    const cfg = (user.permisos || {})[modulo];
    if (!cfg || typeof cfg !== "object" || Array.isArray(cfg)) return true; // legacy mode
    const subs = cfg.submodulos || [];
    if (subs.length === 0) return true; // vacío = todos
    return subs.includes(submodulo);
  };

  const canSeeCosts = () => {
    if (!user || user === false) return false;
    return ["admin", "contabilidad", "gerencia"].includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, login, login2FA, logout, error, setError, hasModule, hasSubmodule, canSeeCosts, serverDown, refetch: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
